import { Injectable } from '@angular/core';
import { Observable, Subject } from 'rxjs';
import tinycolor from 'tinycolor2';
import {ConfigService} from '../config.service';


export interface Color {
    name: string,
    hex: string,
    darkContrast: boolean
}

export interface ColorThemePalette {
    primaryPalette: string,
    accentPalette: string,
    warnPalette: string,
    customProperties: any
}

@Injectable({
    providedIn: 'root',
})
export class ColorPaletteService {

    static readonly componentThemes: string[] = ['primary', 'secondary', 'buttonPrimary', 'buttonSecondary']
    static readonly paletteNames: string[] = ['primary', 'accent', 'warn']
    static readonly themes: string[] = ['light-theme', 'dark-theme']

    private currentTheme: string = 'light-theme'
    private themeEvent: Subject<string> = new Subject<string>()
    public $themeEvent: Observable<string> = new Observable<string>()

    primaryColor: string
    primaryColorPalette: Color[] = []
    accentColor: string
    accentColorPalette: Color[] = []
    warnColor: string
    warnColorPalette: Color[] = []
    static themeToApply: any = {}

    constructor(
        private configService: ConfigService
    ) {
        this.$themeEvent = this.themeEvent.asObservable()
    }

    updateThemePalette(theme?: ColorThemePalette): void {
        ColorPaletteService.themeToApply = theme ?
            theme : this.configService.colorPaletteTheme
        this.savePrimaryPalette(ColorPaletteService.themeToApply.primaryPalette)
        this.saveAccentPalette(ColorPaletteService.themeToApply.accentPalette)
        this.saveWarnPalette(ColorPaletteService.themeToApply.warnPalette)
        this.changeCustomProperties(ColorPaletteService.themeToApply.customProperties)
    }

    savePrimaryPalette(color: string): void {
        this.primaryColor = color
        this.primaryColorPalette = computeColors(this.primaryColor)
        this.saveTypePalette(ColorPaletteService.paletteNames[0])
    }

    saveAccentPalette(color: string): void {
        this.accentColor = color
        this.accentColorPalette = computeColors(this.accentColor)
        this.saveTypePalette(ColorPaletteService.paletteNames[1])
    }

    saveWarnPalette(color: string): void {
        this.warnColor = color
        this.warnColorPalette = computeColors(this.warnColor)
        this.saveTypePalette(ColorPaletteService.paletteNames[2])
    }

    saveTypePalette(paletteName: string): void {
        if (ColorPaletteService.paletteNames.indexOf(paletteName) !== -1) {
            let palette: Color[] = []
            if (paletteName === ColorPaletteService.paletteNames[0]) {
                palette = this.primaryColorPalette
            } else if (paletteName === ColorPaletteService.paletteNames[1]) {
                palette = this.accentColorPalette
            } else if (paletteName === ColorPaletteService.paletteNames[2]) {
                palette = this.warnColorPalette
            }

            if (palette.length > 0) {
                for (const color of palette) {
                    const key1 = `--theme-${paletteName}-${color.name}`
                    const value1 = color.hex
                    const key2 = `--theme-${paletteName}-contrast-${color.name}`
                    const value2 = color.darkContrast ? 'rgba(1, 1, 1, 0.87)' : 'white'
                    document.documentElement.style.setProperty(key1, value1)
                    document.documentElement.style.setProperty(key2, value2)
                }
            }
        }
    }

    switchTheme(): void {
        this.currentTheme = (this.currentTheme === ColorPaletteService.themes[0]) ?
            ColorPaletteService.themes[1] : ColorPaletteService.themes[0]
        this.sendTheme()
    }

    set(customProperty, value): void {
        document.documentElement.style.setProperty(`--${customProperty}`, value)
    }

    sendTheme(): void {
        this.themeEvent.next(this.currentTheme)
    }

    changeCustomProperties(customProperties: any): void {
        for (const customProperty in customProperties) {
            document.documentElement.style.setProperty(`--${customProperty}`, customProperties[customProperty])
        }
    }

    getComponentThemes(themeName: string): any {
        let theme = {}
        if (themeName && ColorPaletteService.themeToApply?.customProperties) {
            switch (themeName) {
                case ColorPaletteService.componentThemes[0]:
                    theme['background-color'] = ColorPaletteService.themeToApply.customProperties['primary-bg']
                    theme['color'] = ColorPaletteService.themeToApply.customProperties['primary-textColor']
                    break
                case ColorPaletteService.componentThemes[1]:
                    theme['background-color'] = ColorPaletteService.themeToApply.customProperties['secondary-bg']
                    theme['color'] = ColorPaletteService.themeToApply.customProperties['secondary-textColor']
                    break
                case ColorPaletteService.componentThemes[2]:
                    theme['background-color'] = ColorPaletteService.themeToApply.customProperties['primary-button-bg']
                    theme['color'] = ColorPaletteService.themeToApply.customProperties['primary-button-textColor']
                    break
                case ColorPaletteService.componentThemes[3]:
                    theme['background-color'] = ColorPaletteService.themeToApply.customProperties['secondary-button-bg']
                    theme['color'] = ColorPaletteService.themeToApply.customProperties['secondary-button-textColor']
                    break
                default:
                    break
            }
        }
        return theme
    }
}

function computeColors(hex: string): Color[] {
    return [
        getColorObject(tinycolor(hex).lighten(52), '50'),
        getColorObject(tinycolor(hex).lighten(37), '100'),
        getColorObject(tinycolor(hex).lighten(26), '200'),
        getColorObject(tinycolor(hex).lighten(12), '300'),
        getColorObject(tinycolor(hex).lighten(6), '400'),
        getColorObject(tinycolor(hex), '500'),
        getColorObject(tinycolor(hex).darken(6), '600'),
        getColorObject(tinycolor(hex).darken(12), '700'),
        getColorObject(tinycolor(hex).darken(18), '800'),
        getColorObject(tinycolor(hex).darken(24), '900'),
        getColorObject(tinycolor(hex).lighten(50).saturate(30), 'A100'),
        getColorObject(tinycolor(hex).lighten(30).saturate(30), 'A200'),
        getColorObject(tinycolor(hex).lighten(10).saturate(15), 'A400'),
        getColorObject(tinycolor(hex).lighten(5).saturate(5), 'A700')
    ]
}

function getColorObject(value, name): Color {
    const c = tinycolor(value)
    return {
        name: name,
        hex: c.toHexString(),
        darkContrast: c.isLight()
    }
}
